// data/quests/questData.js
// Quest definitions - expandable to 100+ quests
// Structure designed for easy additions and quest chains

import { SKILLS } from '../../game/constants.js';

/**
 * Quest NPCs/Givers
 */
export const QUEST_GIVERS = {
    WANDERER: {
        id: 'wanderer',
        name: 'The Wanderer',
        icon: '🧭',
        description: 'A mysterious traveler who knows more than they let on.'
    },
    KEEPER_ASHARA: {
        id: 'keeper_ashara',
        name: 'Keeper Ashara',
        icon: '🔥',
        description: 'Eternal tender of the sacred flames.'
    },
    FLETCHER_ALDRIC: {
        id: 'fletcher_aldric',
        name: 'Master Fletcher Aldric',
        icon: '🏹',
        description: 'Veteran bowyer in the market district.'
    },
    SAGE_MIRAVEL: {
        id: 'sage_miravel',
        name: 'Sage Miravel',
        icon: '✨',
        description: 'Reclusive scholar of the binding arts.'
    },
    CAPTAIN_THORNE: {
        id: 'captain_thorne',
        name: 'Captain Thorne',
        icon: '⚔️',
        description: 'Commander of the realm\'s guard.'
    },
    GUILDMASTER_VEX: {
        id: 'guildmaster_vex',
        name: 'Guildmaster Vex',
        icon: '🗝️',
        description: 'Leader of the Thieves\' Guild.'
    },
    ELDER_BRAMBLE: {
        id: 'elder_bramble',
        name: 'Elder Bramble',
        icon: '🌿',
        description: 'Ancient druid of the groves.'
    },
    FORGEMASTER_KIRA: {
        id: 'forgemaster_kira',
        name: 'Forgemaster Kira',
        icon: '🔨',
        description: 'Master of the Great Forge.'
    },
    ALCHEMIST_NESSA: {
        id: 'alchemist_nessa',
        name: 'Alchemist Nessa',
        icon: '🧪',
        description: 'Keeper of the Apothecary.'
    },
    ARCHITECT_STONEHEART: {
        id: 'architect_stoneheart',
        name: 'Architect Stoneheart',
        icon: '🏗️',
        description: 'Chief builder of the realm.'
    },
    ORACLE_OF_ASHES: {
        id: 'oracle_of_ashes',
        name: 'Oracle of Ashes',
        icon: '👁️',
        description: 'Speaks of what lies beyond the Veil.'
    },
    FISHER_MOREL: {
        id: 'fisher_morel',
        name: 'Fisher Morel',
        icon: '🎣',
        description: 'Old salt with strange tales of the deep.'
    }
};

/**
 * Quest Acts for organization
 */
export const QUEST_ACTS = {
    ACT_1: { id: 'act_1', name: 'Act I: Foundations', questRange: [1, 12] },
    ACT_2: { id: 'act_2', name: 'Act II: Growing Reputation', questRange: [13, 24] },
    ACT_3: { id: 'act_3', name: 'Act III: Trials of Skill', questRange: [25, 36] },
    ACT_4: { id: 'act_4', name: 'Act IV: Veil Touched', questRange: [37, 44] },
    ACT_5: { id: 'act_5', name: 'Act V: Convergence', questRange: [45, 50] }
};

/**
 * All quests
 *
 * Requirements structure:
 * - skills: { skillId: level } - Skill level requirements
 * - items: { itemId: quantity } - Items consumed on start
 * - gold: number - Gold cost (deducted on start)
 * - quests: number[] - Quest numbers that must be completed
 * - questCount: number - Minimum total quests completed
 * - milestones: string[] - Architecting milestone IDs that must be built
 * - pets: { petId: true } - Pets that must be unlocked
 * - petEvolutions: { petId: level } - Pet evolution level requirements
 * - kills: { zone: count } or number - Kill requirements
 *
 * Rewards structure:
 * - items: { itemId: quantity } - Items awarded
 * - unlocks: string[] - Special unlocks (glyph_pouch, quiver_*, zone_*, pet_*)
 */
export const QUESTS = {
    // ==================== ACT I: FOUNDATIONS (1-12) ====================

    1: {
        id: 'first_steps',
        name: 'First Steps',
        act: 1,
        giver: 'wanderer',
        duration: 10 * 60 * 1000, // 10 minutes
        requirements: {},
        rewards: {
            items: { faint_insight: 1 }
        },
        lore: 'Every journey begins somewhere. The Wanderer watches you arrive at the crossroads, nodding slowly. "So, another seeks their fortune in these lands. Let us see what you\'re made of."'
    },

    2: {
        id: 'gathering_trade',
        name: 'The Gathering Trade',
        act: 1,
        giver: 'wanderer',
        duration: 20 * 60 * 1000, // 20 minutes
        requirements: {
            skills: { [SKILLS.MINING]: 5, [SKILLS.WOODCUTTING]: 5, [SKILLS.FISHING]: 5 }
        },
        rewards: {
            items: { faint_insight: 2 }
        },
        lore: 'The land provides for those who know how to take. Stone, timber, fish - the building blocks of survival.'
    },

    3: {
        id: 'flames_introduction',
        name: 'Flames of Introduction',
        act: 1,
        giver: 'keeper_ashara',
        duration: 30 * 60 * 1000, // 30 minutes
        requirements: {
            skills: { [SKILLS.PYRE]: 10 },
            items: { ashes: 50 }
        },
        rewards: {
            items: { faint_support_insight: 2 },
            xpRewards: { [SKILLS.PYRE]: 300 }
        },
        lore: 'The flames flicker in Ashara\'s brazier as she studies you. "You\'ve tended the pyre. Good. The flames remember those who feed them. They will remember you."'
    },

    4: {
        id: 'pilferers_welcome',
        name: 'A Pilferer\'s Welcome',
        act: 1,
        giver: 'guildmaster_vex',
        duration: 25 * 60 * 1000, // 25 minutes
        requirements: {
            skills: { [SKILLS.PILFERING]: 10 }
        },
        rewards: {
            items: { faint_insight: 2 }
        },
        lore: 'Vex grins from the shadows. "Light fingers, eh? The Guild could use someone with your... talents. But trust is earned in our trade."'
    },

    5: {
        id: 'forgemasters_test',
        name: 'The Forgemaster\'s Test',
        act: 1,
        giver: 'forgemaster_kira',
        duration: 45 * 60 * 1000, // 45 minutes
        requirements: {
            skills: { [SKILLS.SMITHING]: 15 },
            items: { pyrestone_bar: 25 },
            gold: 500
        },
        rewards: {
            items: { faint_production_insight: 2 },
            xpRewards: { [SKILLS.SMITHING]: 500 }
        },
        lore: 'Kira\'s hammer rings against the anvil. "Any fool can heat metal. Show me you understand the song of steel."'
    },

    6: {
        id: 'seeds_of_potential',
        name: 'Seeds of Potential',
        act: 1,
        giver: 'elder_bramble',
        duration: 40 * 60 * 1000, // 40 minutes
        requirements: {
            skills: { [SKILLS.FARMING]: 15 },
            items: { potato_seed: 50 }
        },
        rewards: {
            items: { faint_gathering_insight: 2 },
            xpRewards: { [SKILLS.FARMING]: 400 }
        },
        lore: 'The old druid\'s eyes crinkle. "The earth speaks to those who listen. Plant, tend, harvest - this is the oldest magic."'
    },

    7: {
        id: 'tools_of_dedicated',
        name: 'Tools of the Dedicated',
        act: 1,
        giver: 'wanderer',
        duration: 45 * 60 * 1000, // 45 minutes
        requirements: {
            skills: { [SKILLS.MINING]: 15, [SKILLS.WOODCUTTING]: 15, [SKILLS.FISHING]: 15 }
        },
        rewards: {
            items: { smoldering_insight: 1 }
        },
        lore: 'You\'ve moved beyond mere dabbling. The Wanderer sees the calluses on your hands and approves.'
    },

    8: {
        id: 'first_blood',
        name: 'First Blood',
        act: 1,
        giver: 'captain_thorne',
        duration: 45 * 60 * 1000, // 45 minutes
        requirements: {
            skills: { [SKILLS.COMBAT]: 15 },
            gold: 250
        },
        rewards: {
            items: { smoldering_combat_insight: 1 },
            xpRewards: { [SKILLS.COMBAT]: 750 }
        },
        lore: 'Thorne looks you over with a soldier\'s eye. "You\'ve seen battle. Good. The realm needs those who won\'t flinch."'
    },

    9: {
        id: 'binding_arts',
        name: 'The Binding Arts',
        act: 1,
        giver: 'sage_miravel',
        duration: 50 * 60 * 1000, // 50 minutes
        requirements: {
            skills: { [SKILLS.GLYPHBINDING]: 15 },
            items: { flame_glyph: 50 }
        },
        rewards: {
            items: { smoldering_insight: 1 }
        },
        lore: 'Miravel traces patterns in the air. "Glyphs are more than symbols. They are words in a language older than stone. You begin to hear it."'
    },

    10: {
        id: 'brewers_apprentice',
        name: 'Brewer\'s Apprentice',
        act: 1,
        giver: 'alchemist_nessa',
        duration: 50 * 60 * 1000, // 50 minutes
        requirements: {
            skills: { [SKILLS.ALCHEMY]: 15 },
            items: { feral_draft: 20 }
        },
        rewards: {
            items: { smoldering_insight: 1 }
        },
        lore: 'Nessa peers at you through misty spectacles. "Potions are patience made liquid. You show promise."'
    },

    11: {
        id: 'foundation_well_laid',
        name: 'A Foundation Well Laid',
        act: 1,
        giver: 'architect_stoneheart',
        duration: 60 * 60 * 1000, // 1 hour
        requirements: {
            skills: { [SKILLS.ARCHITECTING]: 15 },
            gold: 1000
        },
        rewards: {
            items: { smoldering_insight: 1 }
        },
        lore: 'Stoneheart surveys your work with a critical eye. "Acceptable. Perhaps more than acceptable. The realm grows stronger."'
    },

    12: {
        id: 'rootlings_bond',
        name: 'The Rootling\'s Bond',
        act: 1,
        giver: 'elder_bramble',
        duration: 45 * 60 * 1000, // 45 minutes
        requirements: {
            skills: { [SKILLS.HUSBANDRY]: 15 },
            pets: { rootling: true }
        },
        rewards: {
            items: { smoldering_insight: 1 }
        },
        lore: 'Your first companion. Bramble smiles warmly. "The bond between keeper and creature is sacred. Nurture it."'
    },

    // ==================== ACT II: GROWING REPUTATION (13-24) ====================

    13: {
        id: 'fletchers_notice',
        name: 'The Fletcher\'s Notice',
        act: 2,
        giver: 'fletcher_aldric',
        duration: 60 * 60 * 1000, // 1 hour
        requirements: {
            skills: { [SKILLS.COMBAT]: 25, [SKILLS.WOODWORKING]: 20 },
            items: { arrow_shafts: 100 },
            gold: 2000
        },
        rewards: {
            items: { smoldering_insight: 1 },
            unlocks: ['quiver_leather']
        },
        lore: 'Aldric examines your craftsmanship with grudging respect. "Fine shafts. Here - you\'ve earned this. A proper quiver for a proper archer."'
    },

    14: {
        id: 'whispers_of_veil',
        name: 'Whispers of the Veil',
        act: 2,
        giver: 'keeper_ashara',
        duration: 60 * 60 * 1000, // 1 hour
        requirements: {
            skills: { [SKILLS.PYRE]: 25 },
            items: { ashes: 100 }
        },
        rewards: {
            items: { smoldering_insight: 2 }
        },
        lore: 'Ashara\'s voice drops to a whisper. "The Veil is not just a barrier. It is a promise. Those who tend the flames long enough... they begin to see beyond it."'
    },

    15: {
        id: 'deep_veins',
        name: 'Deep Veins',
        act: 2,
        giver: 'forgemaster_kira',
        duration: 60 * 60 * 1000, // 1 hour
        requirements: {
            skills: { [SKILLS.MINING]: 30 },
            items: { blackiron_ore: 100 },
            gold: 1500
        },
        rewards: {
            items: { smoldering_insight: 2 }
        },
        lore: 'Kira holds the ore to the light. "You\'ve found the deep veins. The earth\'s true treasures. Now we forge something worthy of them."'
    },

    16: {
        id: 'guilds_trust',
        name: 'The Guild\'s Trust',
        act: 2,
        giver: 'guildmaster_vex',
        duration: 60 * 60 * 1000, // 1 hour
        requirements: {
            skills: { [SKILLS.PILFERING]: 25 },
            milestones: ['guild_hall'],
            gold: 2500
        },
        rewards: {
            items: { smoldering_insight: 2 }
        },
        lore: 'Vex slides a key across the table. "You\'ve earned access to the inner circle. The Guild protects its own - and expects the same in return."'
    },

    17: {
        id: 'ancient_timber',
        name: 'Ancient Timber',
        act: 2,
        giver: 'fletcher_aldric',
        duration: 60 * 60 * 1000, // 1 hour
        requirements: {
            skills: { [SKILLS.WOODCUTTING]: 30 },
            items: { ironwood_logs: 100 }
        },
        rewards: {
            items: { kindled_insight: 1 }
        },
        lore: 'Aldric runs his hand along the ironwood grain. "Trees older than kingdoms. This wood remembers things we\'ve forgotten."'
    },

    18: {
        id: 'depths_call',
        name: 'The Depths Call',
        act: 2,
        giver: 'fisher_morel',
        duration: 75 * 60 * 1000, // 1.25 hours
        requirements: {
            skills: { [SKILLS.FISHING]: 30 },
            items: { raw_broadbill: 150 }
        },
        rewards: {
            items: { kindled_insight: 1 }
        },
        lore: 'Old Morel stares at the dark water. "The deep fish know secrets. Sometimes, when the moon is right, they whisper them to me."'
    },

    19: {
        id: 'sacred_preparations',
        name: 'Sacred Preparations',
        act: 2,
        giver: 'keeper_ashara',
        duration: 75 * 60 * 1000, // 1.25 hours
        requirements: {
            skills: { [SKILLS.PYRE]: 35, [SKILLS.COOKING]: 25 },
            items: { ashes: 150 },
            gold: 2000
        },
        rewards: {
            items: { kindled_insight: 1 }
        },
        lore: 'The ritual foods are prepared with reverence. Ashara nods. "The flames are pleased. They burn brighter for you."'
    },

    20: {
        id: 'felltrackers_hunt',
        name: 'Felltracker\'s Hunt',
        act: 2,
        giver: 'elder_bramble',
        duration: 75 * 60 * 1000, // 1.25 hours
        requirements: {
            skills: { [SKILLS.HUSBANDRY]: 30, [SKILLS.COMBAT]: 25 },
            pets: { felltracker: true }
        },
        rewards: {
            items: { kindled_insight: 1 }
        },
        lore: 'The felltracker pads silently beside you. Bramble watches with ancient eyes. "It has chosen you as hunting partner. A rare honor."'
    },

    21: {
        id: 'steel_and_purpose',
        name: 'Steel and Purpose',
        act: 2,
        giver: 'captain_thorne',
        duration: 75 * 60 * 1000, // 1.25 hours
        requirements: {
            skills: { [SKILLS.COMBAT]: 35, [SKILLS.SMITHING]: 30 },
            gold: 3000
        },
        rewards: {
            items: { kindled_combat_insight: 1 },
            xpRewards: { [SKILLS.COMBAT]: 2500, [SKILLS.SMITHING]: 1500 }
        },
        lore: 'Thorne examines your blade. "A warrior is only as good as their steel. And their steel is only as good as their will."'
    },

    22: {
        id: 'apothecarys_secret',
        name: 'The Apothecary\'s Secret',
        act: 2,
        giver: 'alchemist_nessa',
        duration: 75 * 60 * 1000, // 1.25 hours
        requirements: {
            skills: { [SKILLS.ALCHEMY]: 30 },
            milestones: ['greenhouse'],
            items: { clean_emberroot: 50 }
        },
        rewards: {
            items: { kindled_production_insight: 1 },
            xpRewards: { [SKILLS.ALCHEMY]: 2500 }
        },
        lore: 'Nessa gestures to her private garden. "The rarest herbs grow in shadow. Come - I\'ll show you where they hide."'
    },

    23: {
        id: 'glyph_resonance',
        name: 'Glyph Resonance',
        act: 2,
        giver: 'sage_miravel',
        duration: 75 * 60 * 1000, // 1.25 hours
        requirements: {
            skills: { [SKILLS.GLYPHBINDING]: 30 },
            items: { frost_glyph: 30, flame_glyph: 30 }
        },
        rewards: {
            items: { kindled_production_insight: 1 },
            xpRewards: { [SKILLS.GLYPHBINDING]: 2500 }
        },
        lore: 'The glyphs pulse with inner light. Miravel\'s eyes widen. "They sing to each other. And to you. This is... unexpected."'
    },

    24: {
        id: 'blueprint_mastery',
        name: 'Blueprint Mastery',
        act: 2,
        giver: 'architect_stoneheart',
        duration: 75 * 60 * 1000, // 1.25 hours
        requirements: {
            skills: { [SKILLS.ARCHITECTING]: 30 },
            gold: 5000
        },
        rewards: {
            items: { kindled_insight: 1 }
        },
        lore: 'Stoneheart unrolls ancient plans. "These designs were lost for generations. You have the skill to restore them."'
    },

    // ==================== ACT III: TRIALS OF SKILL (25-36) ====================

    25: {
        id: 'secrets_of_pouch',
        name: 'Secrets of the Pouch',
        act: 3,
        giver: 'sage_miravel',
        duration: 90 * 60 * 1000, // 1.5 hours
        requirements: {
            skills: { [SKILLS.GLYPHBINDING]: 40, [SKILLS.COMBAT]: 35 },
            questCount: 10,
            items: { softbark_glyph_core: 5, goldleaf_glyph_core: 3 },
            gold: 10000
        },
        rewards: {
            items: { kindled_insight: 1 },
            unlocks: ['glyph_pouch']
        },
        lore: 'Miravel presents an ancient satchel, humming with power. "Crafted by mages who walked between worlds. It will hold your spells close, ready at a thought."'
    },

    26: {
        id: 'reinforced_path',
        name: 'The Reinforced Path',
        act: 3,
        giver: 'fletcher_aldric',
        duration: 90 * 60 * 1000, // 1.5 hours
        requirements: {
            skills: { [SKILLS.COMBAT]: 40, [SKILLS.WOODWORKING]: 35 },
            quests: [13],
            items: { blackiron_arrows: 200 },
            gold: 5000
        },
        rewards: {
            items: { kindled_insight: 1 },
            unlocks: ['quiver_ironwood']
        },
        lore: 'Aldric stitches the final seam. "Leather over ironwood frame. This quiver has seen a hundred battles and will see a hundred more."'
    },

    27: {
        id: 'embers_awakening',
        name: 'Embers Awakening',
        act: 3,
        giver: 'keeper_ashara',
        duration: 90 * 60 * 1000, // 1.5 hours
        requirements: {
            skills: { [SKILLS.PYRE]: 45 },
            items: { ashes: 200, veil_ember: 10 }
        },
        rewards: {
            items: { kindled_insight: 2 }
        },
        lore: 'Ashara holds an ember that does not fade. "These are fragments of the Veil itself. They remember the first flames, the first light in darkness."'
    },

    28: {
        id: 'ichorebound_pact',
        name: 'Ichorebound Pact',
        act: 3,
        giver: 'elder_bramble',
        duration: 90 * 60 * 1000, // 1.5 hours
        requirements: {
            skills: { [SKILLS.HUSBANDRY]: 45, [SKILLS.GLYPHBINDING]: 30 },
            pets: { ichorebound: true }
        },
        rewards: {
            items: { kindled_insight: 2 }
        },
        lore: 'The ichorebound pulses with arcane energy. Bramble speaks in reverent tones. "Part creature, part magic. The bond runs deeper than blood."'
    },

    29: {
        id: 'wanderers_tale',
        name: 'The Wanderer\'s Tale',
        act: 3,
        giver: 'wanderer',
        duration: 60 * 60 * 1000, // 1 hour
        requirements: {
            questCount: 12
        },
        rewards: {
            items: { kindled_insight: 2 }
        },
        lore: 'The Wanderer settles by the fire. "You\'ve earned a story. Let me tell you of the realm before the Veil, when the first flames were kindled..."'
    },

    30: {
        id: 'forgemasters_challenge',
        name: 'Forgemaster\'s Challenge',
        act: 3,
        giver: 'forgemaster_kira',
        duration: 90 * 60 * 1000, // 1.5 hours
        requirements: {
            skills: { [SKILLS.SMITHING]: 45, [SKILLS.CRAFTING]: 40 },
            items: { verdant_core_bar: 50 },
            gold: 10000
        },
        rewards: {
            items: { blazing_insight: 1 }
        },
        lore: 'Kira\'s hammer falls silent. "You forge as I once did, in my prime. The title of Forgemaster... perhaps one day it will pass to you."'
    },

    31: {
        id: 'shadow_operations',
        name: 'Shadow Operations',
        act: 3,
        giver: 'guildmaster_vex',
        duration: 90 * 60 * 1000, // 1.5 hours
        requirements: {
            skills: { [SKILLS.PILFERING]: 40 },
            questCount: 15,
            gold: 7500
        },
        rewards: {
            items: { blazing_insight: 1 }
        },
        lore: 'Vex hands you a sealed letter. "The Guild has... interests... that require delicate handling. This never happened."'
    },

    32: {
        id: 'tides_and_prophecy',
        name: 'Tides and Prophecy',
        act: 3,
        giver: 'fisher_morel',
        duration: 90 * 60 * 1000, // 1.5 hours
        requirements: {
            skills: { [SKILLS.FISHING]: 45 },
            items: { raw_mako: 250 }
        },
        rewards: {
            items: { blazing_insight: 1 }
        },
        lore: 'Morel\'s voice is distant. "I dreamed of you, you know. Standing at the edge of the Veil, fishing in waters that don\'t exist yet."'
    },

    33: {
        id: 'growth_unbounded',
        name: 'Growth Unbounded',
        act: 3,
        giver: 'elder_bramble',
        duration: 105 * 60 * 1000, // 1.75 hours
        requirements: {
            skills: { [SKILLS.FARMING]: 50 },
            milestones: ['bloomhold_expansion']
        },
        rewards: {
            items: { blazing_insight: 1 }
        },
        lore: 'Bramble touches a seedling that wasn\'t there a moment ago. "The earth senses your devotion. It gives freely to those who give freely."'
    },

    34: {
        id: 'chimeras_trust',
        name: 'The Chimera\'s Trust',
        act: 3,
        giver: 'elder_bramble',
        duration: 105 * 60 * 1000, // 1.75 hours
        requirements: {
            skills: { [SKILLS.HUSBANDRY]: 55, [SKILLS.COMBAT]: 45 },
            pets: { chimera: true },
            petEvolutions: { chimera: 1 }
        },
        rewards: {
            items: { blazing_insight: 1 }
        },
        lore: 'Three heads, one soul. The chimera tests all who approach. Bramble watches you pass its trials. "It has accepted you. Few can claim this."'
    },

    35: {
        id: 'architects_ambition',
        name: 'Architect\'s Ambition',
        act: 3,
        giver: 'architect_stoneheart',
        duration: 105 * 60 * 1000, // 1.75 hours
        requirements: {
            skills: { [SKILLS.ARCHITECTING]: 50 },
            milestones: ['blueprint_studio_i'],
            gold: 15000
        },
        rewards: {
            items: { blazing_insight: 1 }
        },
        lore: 'Stoneheart unveils a grand design. "This is what we build toward. A realm that will stand when all others have fallen."'
    },

    36: {
        id: 'veil_embers_rising',
        name: 'Veil Embers Rising',
        act: 3,
        giver: 'keeper_ashara',
        duration: 120 * 60 * 1000, // 2 hours
        requirements: {
            skills: { [SKILLS.PYRE]: 55 },
            items: { ashes: 300, veil_ember: 25 }
        },
        rewards: {
            items: { blazing_insight: 2 }
        },
        lore: 'Ashara\'s voice trembles. "The embers grow stronger. The Veil thins. Soon, you will see what lies beyond... if you are ready."'
    },

    // ==================== ACT IV: VEIL TOUCHED (37-44) ====================

    37: {
        id: 'hunters_precision',
        name: 'Hunter\'s Precision',
        act: 4,
        giver: 'fletcher_aldric',
        duration: 120 * 60 * 1000, // 2 hours
        requirements: {
            skills: { [SKILLS.COMBAT]: 55, [SKILLS.WOODWORKING]: 50 },
            quests: [26],
            items: { skyiron_arrows: 300 },
            gold: 15000
        },
        rewards: {
            items: { blazing_insight: 1 },
            unlocks: ['quiver_ashen']
        },
        lore: 'Aldric\'s hands shake slightly as he presents the quiver. "Tempered in pyre flames, the leather turned to ash yet holds stronger than steel. It remembers every arrow loosed."'
    },

    38: {
        id: 'oracles_first_vision',
        name: 'Oracle\'s First Vision',
        act: 4,
        giver: 'oracle_of_ashes',
        duration: 90 * 60 * 1000, // 1.5 hours
        requirements: {
            skills: { [SKILLS.PYRE]: 60 },
            questCount: 20
        },
        rewards: {
            items: { blazing_insight: 2 }
        },
        lore: 'The Oracle\'s eyes burn with inner flame. "I have seen you in the ashes. Walking paths that do not yet exist. The Convergence... it waits for you."'
    },

    39: {
        id: 'captains_war',
        name: 'The Captain\'s War',
        act: 4,
        giver: 'captain_thorne',
        duration: 120 * 60 * 1000, // 2 hours
        requirements: {
            skills: { [SKILLS.COMBAT]: 60 },
            gold: 20000
        },
        rewards: {
            items: { blazing_insight: 2 }
        },
        lore: 'Thorne\'s face is grim. "The realm\'s enemies grow bolder. We need warriors who can lead, not just fight. Are you that warrior?"'
    },

    40: {
        id: 'bonds_of_wilds',
        name: 'Bonds of the Wilds',
        act: 4,
        giver: 'elder_bramble',
        duration: 120 * 60 * 1000, // 2 hours
        requirements: {
            skills: { [SKILLS.HUSBANDRY]: 60, [SKILLS.FARMING]: 50, [SKILLS.GATHERER]: 45 },
            petEvolutions: { chimera: 2 }
        },
        rewards: {
            items: { veil_touched_insight: 1 }
        },
        lore: 'Bramble\'s form shimmers. "The natural world and the Veil are not so different. Both are cycles. Both are eternal. You understand this now."'
    },

    41: {
        id: 'into_the_crucible',
        name: 'Into the Crucible',
        act: 4,
        giver: 'keeper_ashara',
        duration: 150 * 60 * 1000, // 2.5 hours
        discoveryLevel: 60, // Pyre level at which quest becomes "discovered" in UI
        requirements: {
            skills: { [SKILLS.PYRE]: 70, [SKILLS.COMBAT]: 65 },
            quests: [36],
            items: { veil_ember: 50 },
            gold: 25000
        },
        rewards: {
            items: { veil_touched_insight: 1 },
            unlocks: ['zone_ashen_crucible_t1']
        },
        lore: 'The Crucible has burned since before memory. Three guardians stand between you and the deeper flames. Prove yourself against the Warden, the Guardian, and the Eternal.'
    },

    42: {
        id: 'guilds_ultimate_heist',
        name: 'Guild\'s Ultimate Heist',
        act: 4,
        giver: 'guildmaster_vex',
        duration: 150 * 60 * 1000, // 2.5 hours
        requirements: {
            skills: { [SKILLS.PILFERING]: 60 },
            milestones: ['guild_storage_ii'],
            questCount: 25,
            gold: 30000
        },
        rewards: {
            items: { veil_touched_insight: 1 }
        },
        lore: 'Vex\'s grin is predatory. "There\'s a vault. Ancient. Protected by magic older than the Guild. No one\'s ever cracked it. Until now."'
    },

    43: {
        id: 'sages_revelation',
        name: 'The Sage\'s Revelation',
        act: 4,
        giver: 'sage_miravel',
        duration: 150 * 60 * 1000, // 2.5 hours
        requirements: {
            skills: { [SKILLS.GLYPHBINDING]: 60 },
            items: { stone_glyph: 50, storm_glyph: 50 },
            unlocks: ['glyph_pouch']
        },
        rewards: {
            items: { veil_touched_insight: 1 }
        },
        lore: 'Miravel\'s hands trace patterns in the air. "The glyphs... they\'re not just magic. They\'re words from beyond the Veil. And I\'ve finally learned to read them."'
    },

    44: {
        id: 'alchemists_magnum_opus',
        name: 'The Alchemist\'s Magnum Opus',
        act: 4,
        giver: 'alchemist_nessa',
        duration: 150 * 60 * 1000, // 2.5 hours
        requirements: {
            skills: { [SKILLS.ALCHEMY]: 60 },
            items: { hardened_infusion: 100, rallying_elixir: 50 }
        },
        rewards: {
            items: { veil_touched_insight: 1 }
        },
        lore: 'Nessa holds a vial of liquid light. "A lifetime of work. A potion that touches the Veil itself. And you helped make it possible."'
    },

    // ==================== ACT V: CONVERGENCE (45-50) ====================

    45: {
        id: 'communion_of_spirits',
        name: 'Communion of Spirits',
        act: 5,
        giver: 'elder_bramble',
        duration: 180 * 60 * 1000, // 3 hours
        requirements: {
            skills: { [SKILLS.HUSBANDRY]: 75, [SKILLS.COMBAT]: 70, [SKILLS.PYRE]: 60, [SKILLS.GATHERER]: 55, [SKILLS.GLYPHBINDING]: 50 },
            quests: [40],
            items: { veil_ember: 100 },
            gold: 50000
        },
        rewards: {
            items: { veil_touched_insight: 2 },
            unlocks: ['pet_eidolon']
        },
        lore: 'A figure of pure light materializes. Bramble weeps. "An eidolon. A spirit from beyond the Veil. It has chosen you as its anchor in this world."'
    },

    46: {
        id: 'masters_arsenal',
        name: 'Master\'s Arsenal',
        act: 5,
        giver: 'fletcher_aldric',
        duration: 180 * 60 * 1000, // 3 hours
        requirements: {
            skills: { [SKILLS.COMBAT]: 70, [SKILLS.WOODWORKING]: 65 },
            quests: [37],
            items: { verdant_core_arrows: 500 },
            gold: 40000
        },
        rewards: {
            items: { veil_touched_insight: 1 },
            unlocks: ['quiver_veilstalker']
        },
        lore: 'Aldric is old now, his hands gnarled. But they do not shake as he presents the quiver. "My legacy. Everything I know, everything I was. It\'s yours now."'
    },

    47: {
        id: 'depths_of_devotion',
        name: 'Depths of Devotion',
        act: 5,
        giver: 'keeper_ashara',
        duration: 180 * 60 * 1000, // 3 hours
        requirements: {
            skills: { [SKILLS.PYRE]: 80 },
            questCount: 35,
            items: { veil_ember: 100 }
        },
        rewards: {
            items: { convergent_insight: 1 }
        },
        lore: 'Ashara\'s voice is fire itself. "You have walked the path of flames longer than most. The Veil knows you. It calls to you."'
    },

    48: {
        id: 'where_flames_return',
        name: 'Where All Flames Return',
        act: 5,
        giver: 'oracle_of_ashes',
        duration: 240 * 60 * 1000, // 4 hours
        discoveryLevel: 78, // Pyre level at which quest becomes "discovered" in UI
        requirements: {
            skills: { [SKILLS.PYRE]: 85, [SKILLS.COMBAT]: 80 },
            quests: [59],  // Trial of the Three Flames
            bossDefeats: ['searing_matriarch', 'molten_colossus', 'cindered_knight'],
            items: { veil_ember: 250 },
            gold: 100000
        },
        rewards: {
            items: { convergent_insight: 2 },
            unlocks: ['zone_ashen_crucible_t3']
        },
        lore: 'The Oracle speaks in a thousand voices. "The Convergence is not an end. It is a beginning. All flames return here. All flames begin here. Are you ready to see?"'
    },

    49: {
        id: 'legacy_of_realm',
        name: 'Legacy of the Realm',
        act: 5,
        giver: 'architect_stoneheart',
        duration: 210 * 60 * 1000, // 3.5 hours
        requirements: {
            skills: { [SKILLS.ARCHITECTING]: 85 },
            milestones: ['reinforced_storage_iii'],
            questCount: 40,
            gold: 75000
        },
        rewards: {
            items: { convergent_insight: 2 }
        },
        lore: 'Stoneheart stands before a monument. "Your name will be carved here. Among the realm\'s greatest builders. For all time."'
    },

    50: {
        id: 'realms_champion',
        name: 'Realm\'s Champion',
        act: 5,
        giver: 'wanderer',
        duration: 300 * 60 * 1000, // 5 hours
        requirements: {
            skills: { [SKILLS.COMBAT]: 90 },
            questCount: 49,
            unlocks: ['quiver_veilstalker', 'pet_eidolon']
        },
        rewards: {
            items: { convergent_insight: 5 }
        },
        lore: 'The Wanderer removes their hood. Their face is light itself. "I have watched you since the beginning. You have become what I hoped. The realm is safe in your hands. But the Veil... the Veil is only the beginning."'
    },

    // ==================== ADDITIONAL QUESTS (51+) ====================

    51: {
        id: 'first_feast',
        name: 'The First Feast',
        act: 1,
        giver: 'wanderer',
        duration: 35 * 60 * 1000, // 35 minutes
        requirements: {
            skills: { [SKILLS.COOKING]: 15 },
            items: { cooked_sand_smelt: 50 }
        },
        rewards: {
            items: { smoldering_insight: 1 }
        },
        lore: 'The Wanderer tends a small fire. "Survival is not just gathering - it\'s preparing. A full belly makes the journey easier."'
    },

    52: {
        id: 'wanderers_second_lesson',
        name: 'The Wanderer\'s Second Lesson',
        act: 1,
        giver: 'wanderer',
        duration: 15 * 60 * 1000, // 15 minutes
        requirements: {
            quests: [1, 2],
            skills: { [SKILLS.MINING]: 10, [SKILLS.WOODCUTTING]: 10, [SKILLS.FISHING]: 10 }
        },
        rewards: {},
        lore: 'The Wanderer sits in silence for a long moment. "You have learned to take from the land. Now learn patience. The path ahead is longer than you know."'
    },

    53: {
        id: 'architects_vision',
        name: 'The Architect\'s Vision',
        act: 2,
        giver: 'architect_stoneheart',
        duration: 55 * 60 * 1000, // 55 minutes
        requirements: {
            skills: { [SKILLS.ARCHITECTING]: 20, [SKILLS.CRAFTING]: 15 },
            gold: 1500
        },
        rewards: {
            items: { smoldering_insight: 2 }
        },
        lore: 'Stoneheart spreads rough sketches across the table. "Before the grand designs, there must be vision. Show me you can see what isn\'t there yet."'
    },

    54: {
        id: 'alchemists_garden',
        name: 'The Alchemist\'s Garden',
        act: 2,
        giver: 'alchemist_nessa',
        duration: 65 * 60 * 1000, // 65 minutes
        requirements: {
            skills: { [SKILLS.FARMING]: 25, [SKILLS.ALCHEMY]: 20 },
            items: { clean_ironbloom: 30, clean_duskleaf: 30 }
        },
        rewards: {
            items: { kindled_insight: 1 }
        },
        lore: 'Nessa examines your herbs with keen eyes. "Grown by your own hand, cleaned by your own care. The best potions start with the best ingredients."'
    },

    55: {
        id: 'expedition_supplies',
        name: 'Expedition Supplies',
        act: 2,
        giver: 'captain_thorne',
        duration: 70 * 60 * 1000, // 70 minutes
        requirements: {
            skills: { [SKILLS.GATHERER]: 25, [SKILLS.COOKING]: 25 },
            quests: [52],
            items: { cooked_broadbill: 75 }
        },
        rewards: {
            items: { kindled_insight: 1 }
        },
        lore: 'Thorne inspects the provisions. "My scouts need supplies that travel well. You\'ve proven you can provide. The realm\'s reach extends because of efforts like yours."'
    },

    56: {
        id: 'tools_of_war',
        name: 'Tools of War',
        act: 2,
        giver: 'captain_thorne',
        duration: 70 * 60 * 1000, // 70 minutes
        requirements: {
            skills: { [SKILLS.SMITHING]: 30, [SKILLS.COMBAT]: 25 },
            items: { skyiron_bar: 50 },
            gold: 2000
        },
        rewards: {
            items: { kindled_insight: 1 }
        },
        lore: 'Thorne hefts a blade you\'ve forged. "A warrior who smiths their own steel understands its limits. And its potential."'
    },

    57: {
        id: 'threshold_of_mastery',
        name: 'Threshold of Mastery',
        act: 3,
        giver: 'wanderer',
        duration: 30 * 60 * 1000, // 30 minutes
        requirements: {
            questCount: 18,
            skills: { [SKILLS.MINING]: 40, [SKILLS.COMBAT]: 40, [SKILLS.PYRE]: 35 }
        },
        rewards: {},
        lore: 'The Wanderer studies you with ancient eyes. "You stand at a threshold. Beyond it, the trials grow harder. The rewards, greater. Step forward only when you are ready."'
    },

    58: {
        id: 'whispers_in_deep',
        name: 'Whispers in the Deep',
        act: 3,
        giver: 'fisher_morel',
        duration: 95 * 60 * 1000, // 95 minutes
        requirements: {
            skills: { [SKILLS.FISHING]: 45 },
            quests: [57],
            items: { raw_mako: 100 }
        },
        rewards: {
            items: { blazing_insight: 1 }
        },
        lore: 'Morel\'s voice drops low. "The deep fish... they\'ve been acting strange. Swimming in patterns. Like they\'re trying to tell us something. Or warn us."'
    },

    // ==================== ASHEN CRUCIBLE QUEST CHAIN ====================

    59: {
        id: 'trial_of_three_flames',
        name: 'Trial of the Three Flames',
        act: 5,
        giver: 'oracle_of_ashes',
        duration: 180 * 60 * 1000, // 3 hours
        discoveryLevel: 70, // Pyre level at which quest becomes "discovered" in UI
        requirements: {
            skills: { [SKILLS.PYRE]: 78, [SKILLS.COMBAT]: 75 },
            quests: [41],  // Into the Crucible
            bossDefeats: ['ashen_warden', 'pyre_guardian', 'flame_eternal'],
            items: { veil_ember: 150 },
            gold: 50000
        },
        rewards: {
            items: { convergent_insight: 1 },
            unlocks: ['zone_ashen_crucible_t2']
        },
        lore: 'You have bested the guardians. But the Crucible runs deeper still. Three ancient champions await — each bound to a different flame. To face the one you seek, you must master the style they fear.'
    },

    // ==================== CONTRACTOR QUEST ====================

    60: {
        id: 'carpenters_arrival',
        name: 'The Carpenter\'s Arrival',
        act: 1,
        giver: 'architect_stoneheart',
        duration: 30 * 60 * 1000, // 30 minutes
        requirements: {
            skills: { [SKILLS.ARCHITECTING]: 5 },
            gold: 500
        },
        rewards: {
            unlocks: ['contractor_hired']
        },
        lore: 'After watching you fumble through yet another lopsided chair, Architect Stoneheart has seen enough. He knows a traveling carpenter — a real craftsman — looking for steady work. For a modest fee, this contractor can handle the actual building while you focus on... well, anything else. Your carpentry is a danger to the town.'
    }
};

/**
 * Get quest by number
 */
export function getQuest(questNumber) {
    return QUESTS[questNumber] || null;
}

/**
 * Get quest giver info
 */
export function getQuestGiver(giverId) {
    return Object.values(QUEST_GIVERS).find(g => g.id === giverId) || null;
}

/**
 * Get all quests in an act
 */
export function getQuestsByAct(actNumber) {
    return Object.entries(QUESTS)
        .filter(([num, quest]) => quest.act === actNumber)
        .map(([num, quest]) => ({ number: parseInt(num), ...quest }));
}

/**
 * Get total quest count
 */
export function getTotalQuestCount() {
    return Object.keys(QUESTS).length;
}

export default {
    QUEST_GIVERS,
    QUEST_ACTS,
    QUESTS,
    getQuest,
    getQuestGiver,
    getQuestsByAct,
    getTotalQuestCount
};
